﻿{
  "shared": {
    "meta": {
      "robots": "index, follow",
      "og:title": "{{title}}",
      "og:description": "{{description}}",
      "og:url": "{{canonical}}",
      "og:type": "website",
      "og:site_name": "CBRE"
    }
  },
  "pages": {
    "home": {
      "route": {
        "path": "/tools/office-space-calculator"
      },
      "head": {
        "title": "Office Space Calculator | CBRE Spacer",
        "meta": {
          "description": "Find out how much office space your company needs. CBRE's office space calculator gives you a custom size estimate based on your team, workstyle and goals.",
          "og:title": "Office Space Calculator for Growing Teams | CBRE Spacer",
          "og:description": "Get a custom office size recommendation in minutes."
        },
        "canonical": "/tools/office-space-calculator",
        "schema": {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "CBRE Office Space Calculator",
          "url": "https://www.cbre.com/tools/office-space-calculator",
          "description": "Free office space calculator. Get a custom size recommendation based on your team, workstyle and goals.",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "provider": {
            "@type": "Organization",
            "name": "CBRE",
            "url": "https://www.cbre.com"
          }
        }
      }
    },
    "spacePlanningFAQs": {
      "route": {
        "path": "/tools/office-space-calculator/space-planning-faqs"
      },
      "head": {
        "title": "Space Planning FAQs | CBRE Spacer",
        "meta": {
          "description": "Answers to common office space planning questions - how much space you need, how to calculate it and what RSF and USF mean for your search."
        },
        "canonical": "/tools/office-space-calculator/space-planning-faqs"
      }
    },
    "howDoesSpacerWork": {
      "route": {
        "path": "/tools/office-space-calculator/space-planning-faqs/how-does-spacer-work"
      },
      "head": {
        "title": "How Does the Office Space Calculator Work? | CBRE Spacer",
        "meta": {
          "description": "CBRE's office space calculator asks a few questions about your team and goals, then gives you a recommended office size, a space breakdown and the benchmarks behind each figure."
        },
        "canonical": "/tools/office-space-calculator/space-planning-faqs/how-does-spacer-work",
        "schema": {
          "@context": "https://schema.org",
          "@type": [
            "FAQPage",
            "Article"
          ],
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How does an office space calculator work?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "An office space calculator asks questions about your team size, workstyle and goals, then produces a recommended office size and space breakdown based on workplace benchmarks. CBRE's calculator takes around 15 minutes and gives you a full space program including square footage, room types and seat ratios."
              }
            },
            {
              "@type": "Question",
              "name": "What do you get from CBRE's office space calculator?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You receive a recommended office size in square feet or metres, a breakdown of space types and quantities and the density and seat ratio benchmarks behind each figure. Results take around 15 minutes."
              }
            },
            {
              "@type": "Question",
              "name": "What is a space program?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "A space program — a document that defines what your office needs to contain and how big it should be — covers the mix of desks, meeting rooms, collaboration areas and support spaces. Brokers use it to shortlist properties and architects use it to plan layouts."
              }
            }
          ]
        }
      }
    },
    "whyDefiningYourPerfectOfficeMatters": {
      "route": {
        "path": "/tools/office-space-calculator/space-planning-faqs/why-defining-your-perfect-office-matters"
      },
      "head": {
        "title": "How Much Office Space Does Your Company Need? | CBRE Spacer",
        "meta": {
          "description": "Choosing the right office size affects your team's productivity, culture and costs. Here's how to figure out what your company needs - and why a headcount formula isn't enough."
        },
        "canonical": "/tools/office-space-calculator/space-planning-faqs/why-defining-your-perfect-office-matters",
        "schema": {
          "@context": "https://schema.org",
          "@type": [
            "FAQPage",
            "Article"
          ],
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How much office space do I need?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Most companies use 100–150 sq ft per person as a starting point, but the right amount depends on how your team works, your attendance patterns and the types of space you need. CBRE's office space calculator gives you a tailored recommendation based on your specific situation."
              }
            },
            {
              "@type": "Question",
              "name": "How much office space per person is standard?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The typical benchmark is 100–150 sq ft per person in usable square footage. This varies based on office type, density model and how much shared and collaboration space is included."
              }
            },
            {
              "@type": "Question",
              "name": "Does hybrid working change how much office space I need?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. If not all employees are in the office at the same time, you may need fewer dedicated desks and more shared or flexible workspace. An office space calculator can model this for you."
              }
            }
          ]
        }
      }
    },
    "differenceBetweenUsableRentableSquareFeet": {
      "route": {
        "path": "/tools/office-space-calculator/space-planning-faqs/difference-between-usable-rentable-square-feet"
      },
      "head": {
        "title": "Rentable vs. Usable Square Feet: What's the Difference? | CBRE Spacer",
        "heading": "Rentable vs. Usable Square Feet: What's the Difference?",
        "meta": {
          "description": "Confused by RSF and USF? Here's what each term means, why it matters when leasing office space and how to use both figures in your search."
        },
        "canonical": "/tools/office-space-calculator/space-planning-faqs/difference-between-usable-rentable-square-feet",
        "schema": {
          "@context": "https://schema.org",
          "@type": [
            "FAQPage",
            "Article"
          ],
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is the difference between rentable and usable square feet?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Usable square feet (USF) is the space your team actually occupies. Rentable square feet (RSF) includes USF plus a proportional share of building common areas. RSF is what landlords quote and what you pay rent on. USF is what you use for space planning and layout design."
              }
            },
            {
              "@type": "Question",
              "name": "What is a loss factor in office leasing?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "A loss factor is the ratio between a building's rentable and usable square footage. A 15% loss factor means you pay rent on 115 RSF for every 100 USF you occupy. Loss factors vary by building and market."
              }
            },
            {
              "@type": "Question",
              "name": "Which should I use when searching for office space — RSF or USF?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Use RSF when searching for space and comparing listings — it is the figure landlords quote and the one your lease will be based on. Use USF when planning your layout — it tells you how much space you actually have to work with. CBRE's office space calculator gives you results in both."
              }
            }
          ]
        }
      }
    },
    "terms": {
      "route": {
        "path": "/tools/office-space-calculator/terms-spacer-by-cbre"
      },
      "head": {
        "title": "Terms of Service - Spacer by CBRE",
        "meta": {
          "description": "Terms of service for CBRE Spacer, the office space calculator built on CBRE's workplace expertise."
        },
        "canonical": "/tools/office-space-calculator/terms-spacer-by-cbre"
      }
    },
    "quiz": {
      "route": {
        "path": "/tools/office-space-calculator/quiz-spacer-by-cbre"
      },
      "head": {
        "title": "Office Space Quiz | CBRE Spacer",
        "meta": {
          "description": "Answer a few questions about your team and goals. Get an office size recommendation based on CBRE's workplace benchmarks."
        },
        "canonical": "/tools/office-space-calculator/quiz-spacer-by-cbre"
      }
    },
    "results": {
      "route": {
        "path": "/tools/office-space-calculator/program-your-results-spacer-by-cbre"
      },
      "head": {
        "title": "Your Office Size Recommendation | CBRE Spacer",
        "meta": {
          "description": "Your custom office size recommendation from CBRE Spacer. Use your results to start your property search or connect with a CBRE broker."
        },
        "canonical": "/tools/office-space-calculator/program-your-results-spacer-by-cbre"
      }
    },
    "resultsWithId": {
      "route": {
        "path": "/tools/office-space-calculator/program-your-results-spacer-by-cbre/:id"
      },
      "head": {
        "title": "Your Office Size Recommendation | CBRE Spacer",
        "meta": {
          "description": "Your custom office size recommendation from CBRE Spacer. Use your results to start your property search or connect with a CBRE broker."
        },
        "canonical": "/tools/office-space-calculator/program-your-results-spacer-by-cbre"
      }
    },
    "profileComparison": {
      "route": {
        "path": "/tools/office-space-calculator/profile-comparison-spacer-by-cbre"
      },
      "head": {
        "title": "Compare Office Profiles | CBRE Spacer",
        "meta": {
          "description": "See how your office profile compares. Powered by CBRE's workplace benchmarks."
        },
        "canonical": "/tools/office-space-calculator/profile-comparison-spacer-by-cbre"
      }
    },
    "referral": {
      "route": {
        "path": "/tools/office-space-calculator/referral"
      },
      "head": {
        "title": "Referral Link for CBRE Brokers - Spacer By CBRE",
        "meta": {
          "description": "Use this referral link to send Spacer to clients and ensure their space results are sent directly to your email. Learn more here."
        },
        "canonical": "/tools/office-space-calculator/referral"
      }
    },
    "referralSourceLanding": {
      "route": {
        "path": "/tools/office-space-calculator/referral/:sourceId"
      },
      "head": {
        "title": "Office Space Calculator for Growing Teams | CBRE Spacer",
        "meta": {
          "description": "Find out how much office space your company needs. CBRE's office space calculator gives you a custom size estimate based on your team, workstyle and goals.",
          "og:title": "Office Space Calculator for Growing Teams | CBRE Spacer",
          "og:description": "Get a custom office size recommendation in minutes."
        },
        "canonical": "/tools/office-space-calculator"
      }
    },
    "referralRetrieve": {
      "route": {
        "path": "/tools/office-space-calculator/referral/retrieve"
      },
      "head": {
        "title": "Retrieve Referral Link for CBRE Brokers - Spacer By CBRE",
        "meta": {
          "description": "Retrieve your referral link for Spacer."
        },
        "canonical": "/tools/office-space-calculator/referral/retrieve"
      }
    }
  }
}
